import {ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {AddCommentComponent} from '../modal/add-comment/add-comment.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ProductModel, RequestParamModel} from '@coremodels';
import {ProductsService, QuoteOrderSampleService, StockAllocationService} from '@coreapiservices';
import {HttpErrorResponse} from '@angular/common/http';
import {PreviewDetailComponent} from 'src/app/quote-order-sample-core/preview-detail/preview-detail.component';
import {ConfirmModalComponent} from '../../admin-core/components/confirm-modal/confirm-modal.component';
import {BsModalService} from 'ngx-bootstrap/modal';
import {TransferProductModalComponent} from '../modal/transfer-product-modal/transfer-product-modal.component';
import {Subject} from 'rxjs';
import {debounceTime, filter, takeUntil} from 'rxjs/operators';
import {AlertService, DebugLoggerService, SubscribersService, WebSocketService} from '@coreservices';
import {Constants} from '../../admin-core/constants';
import {QuoteService} from 'src/app/admin-core/apiservices/quote.service';
import {MoiWarningModalComponent} from '../../shared/modal/moi-warning-modal/moi-warning-modal.component';
import {extractErrorMessage} from '../../admin-core/utils/error-message.util';
import {containerDelayHistory, openContainerDelayHistory} from '../../admin-core/utils/container-delay-history.util';

@Component({
  standalone: false,
    selector: 'app-stock-allocation-content',
    // Eager: Angular 22 defaults every component to OnPush, which would leave this view
    // unpainted after its subscribe callbacks (see app.component.ts for the full note).
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './stock-allocation-content.component.html',
    styleUrls: ['./stock-allocation-content.component.css', '../stock-allocation.component.css']
})
export class StockAllocationContentComponent implements OnInit {

    // Default to undefined so the template can distinguish "data not yet assigned" from "data assigned to empty".
    // The skeleton stays up until these are explicitly set in the API success / error handlers.
    @Input() unassignedStock: any;
    @Input() stockOnHand: any;
    @Input() isDashboard: any;
    @Input() productId: any;
    @Input() uom: any;
    @Input() stock_available: any;
    @Input() containerId: any;
    /**
     * The order line's own container as the ORDER API sent it, passed only for a closed line
     * (order archived / line already at the warehouse) that keeps the dates it was closed with.
     * The container cards here come from the product endpoint, which cannot know which order is
     * looking and always returns the container's live dates — see withLineDates.
     */
    @Input() lineContainer: any = null;
    /**
     * The same, for the whole order: container id -> the container as the ORDER API sent it on a
     * closed line of this order. Covers the cards of containers this line is not on.
     */
    @Input() orderFrozenContainers: Record<number, any> | null = null;
    @Input() orderId: any;
    @Input() exportId: any;
    @Input() isContainerManagement: any;
    @Input() activeTab: any;
    @Input() quoteProduct: any;
    @Input() containers: any = [];
    @Input() activeIndex: any = null;
    @Input() isProductTable: any = false;
    @Input() disableDragAndDropBasedOnOrder: boolean = false;
    @Input() disableDragAndDrop: boolean = false;
    @Input() isSummary: boolean = false;
    @Input() isReadOnly: boolean = false;
    @Input() frontendUserId: any;
    /*
     * Assembled products — COMPONENT MODE.
     *
     * Set to { quoteProductId, componentProductId } to draw this same card set for ONE COMPONENT of
     * an assembled order line instead of for a product of its own. Everything the user sees — the
     * cards, their figures, the drag gesture, the split modal, the skeleton — is the ordinary
     * allocation screen; only where the rows come from and what a drop writes are different, because
     * a component has no quote_product rows of its own (its demand belongs to the assembled line).
     *
     * Null for every ordinary caller, which is what keeps their path unchanged.
     */
    @Input() componentAllocation: any = null;
    /*
     * Assembled products — UNIT MODE.
     *
     * Set to { quoteProductId } to draw this card set for an ASSEMBLED ORDER LINE counted in
     * assembled units: each card shows how many whole units that source can complete, and a drop
     * allocates every component of those units together. Component quantities never appear — they
     * are in each card's tooltip.
     *
     * Or { productId } for the product with no order line in view (By Product): the same cards,
     * with no row flagged as "this order". A drop still moves the dragged row's own line.
     *
     * Null for every other caller, which is what keeps their path unchanged.
     */
    @Input() assembledUnitBoard: any = null;
    /**
     * Keeps the dark Stock Allocation card skin for a caller that is not `isDashboard` — the
     * assembled board on the Stock Allocation page. `isDashboard` cannot be reused for that: it
     * also changes drag selection and which events fire. Order details leaves this off and stays light.
     */
    @Input() darkCards = false;
    /** Unit mode: the board's cards, keyed by card key — each card's (i) explains its own figure. */
    unitCards: any = {};
    /** Unit mode: where the shelf figures came from (the board's `unleashed`), for the Stock On Hand (i). */
    unitBoardUnleashed: any = null;
    /*
     * Why this line cannot be allocated at all — a recipe not yet fetched from Unleashed, most
     * often. Shown instead of the cards: an empty card strip with no explanation reads as a broken
     * screen, when in fact there is nothing wrong that allocating could fix.
     */
    unitBoardBlockedReason: string | null = null;
    /** Unit mode: the assembled product on the board, to match live stock events against. */
    private unitBoardProductId: number | null = null;
    private watchingAssembledStock = false;
    @Output("containerSidebarClickEvent") containerSidebarClickEvent: EventEmitter<any> = new EventEmitter();
    @Output("selectedContainerEvent") selectedContainerEvent: EventEmitter<any> = new EventEmitter();
    @Output("selectedProductAddContainerEvent") selectedProductAddContainerEvent: EventEmitter<any> = new EventEmitter();
    @Output("currentQuantityDataEvent") currentQuantityDataEvent: EventEmitter<any> = new EventEmitter();
    @Output("productDetailRefresh") productDetailRefresh: EventEmitter<any> = new EventEmitter();
    @Output("dropActionCompleted") dropActionCompleted: EventEmitter<any> = new EventEmitter();
    @Output("orderDetailRefresh") orderDetailRefresh: EventEmitter<any> = new EventEmitter();

    selectedCard: any;
    isdragging = null;
    constants = Constants;
    requestParamModel = new RequestParamModel();
    quantityData: any;
    sourceArray = [];
    sourceIndex = -1;
    filters = [{id: 1, name: 'South Windsor'}];
    draggedProduct = null;
    draggedData: any = null; // Tracks currently dragged row data for custom drag preview
    destroy$ = new Subject<void>();
    isLoading = false;
    isUnassignedStockLoading = true;
    isStockOnHandLoading = true;
    isQuantityLoading = true;
    isInitialLoad = true; // ✅ Track if this is the first load

    constructor(
        private modalService: NgbModal,
        private quoteServices: QuoteOrderSampleService,
        private productServices: ProductsService,
        private bsModalService: BsModalService,
        private stockAllocationService: StockAllocationService,
        private alertService: AlertService,
        private changeDetection: ChangeDetectorRef,
        private subscribersService: SubscribersService,
        private quoteService: QuoteService,
        private webSocketService: WebSocketService,
        private debugLogger: DebugLoggerService
    ) {
        this.stockAllocationService.draggedProduct$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
            this.draggedProduct = id;
        });
        effect(() => {
            if (this.subscribersService.mergeContainer()) {
                let details = this.subscribersService.mergeContainer();
                this.mergeProduct(details.index, details.container);
            }
        });
        // ✅ Subscribe to quoteOrderSignal to refresh stock allocation after SRD update
        effect(() => {
            // Acts on every tick. Do NOT reset the signal here — other listeners read the same value.
            // Assembled/component modes own their own refresh.
            if (this.subscribersService.quoteOrderSignal() > 0 && !this.componentAllocation && !this.assembledUnitBoard) {
                this.debugLogger.log('🔄 quoteOrderSignal triggered - refreshing stock allocation data');
                this.refreshStockData();
            }
        });
    }

    /**
     * Re-read unassigned stock, on-hand stock and every container from the server.
     *
     * The plain-move path updates its arrays in place instead, but a split has to re-fetch: the new
     * line is created server-side, so the client has no row to insert until it asks.
     */
    private refreshStockData(): void {
        this.getStock('unassigned_stock');
        this.getStock('on_hand_stock');
        if (this.containers?.length) {
            this.containers.forEach((container) => {
                this.getStock(null, container);
            });
        }
        this.getQuantity();
    }

    ngOnInit(): void {
        if (this.assembledUnitBoard) {
            this.loadAssembledUnitBoard();
            return;
        }

        if (this.componentAllocation) {
            this.loadComponentBoard();
            return;
        }

        if (this.quoteProduct) {
            this.startInitialLoad();
            this.getStock('unassigned_stock');
            this.getStock('on_hand_stock');
            if (this.quoteProduct.containers && this.quoteProduct.containers.length > 0) {
                this.quoteProduct.containers?.map((container) => {
                    this.getStock(null, container);
                })
            }
            this.containers = this.quoteProduct.containers || [];
            this.getQuantity();
        } else if (!this.isInitialPanelFetch()) {
            // No API call will fire from this lifecycle — drop the skeleton so static content renders.
            this.isInitialLoad = false;
            // Mark stock state as assigned (empty) so shouldShowSkeleton resolves to false.
            if (this.unassignedStock === undefined) this.unassignedStock = [];
            if (this.stockOnHand === undefined) this.stockOnHand = [];
        }

        // Calculate container quantities if containers are passed from parent (isProductTable scenario)
        if (this.isProductTable && this.containers?.length > 0) {
            this.containers.forEach(container => {
                this.calculateContainerQuantities(container);
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.assembledUnitBoard) {
            if (changes['assembledUnitBoard'] && !changes['assembledUnitBoard'].firstChange) {
                this.loadAssembledUnitBoard();
            }
            return;
        }

        if (this.componentAllocation) {
            // Re-read whenever the caller points this card set at a different component.
            if (changes['componentAllocation'] && !changes['componentAllocation'].firstChange) {
                this.loadComponentBoard();
            }
            return;
        }

        if (this.activeTab == 1 && changes['productId'] && changes['productId'].currentValue) {
            // Reset loading flags so the skeleton renders for both the initial expansion
            // and any subsequent productId switches (e.g. dashboard sidebar selection).
            // Socket-driven refreshes go through the quoteOrderSignal effect, which calls
            // getStock without touching these flags, so the skeleton does not reappear there.
            this.startInitialLoad();
            this.getStock('unassigned_stock');
            this.getStock('on_hand_stock');
            if (this.containers?.length) {
                this.containers.forEach((container) => {
                    this.getStock(null, container);
                })
            }
            this.getQuantity();
        }
    }

    private startInitialLoad() {
        this.isInitialLoad = true;
        this.isUnassignedStockLoading = true;
        this.isStockOnHandLoading = true;
        this.isQuantityLoading = true;
    }

    private isInitialPanelFetch(): boolean {
        return !!this.quoteProduct || (this.activeTab == 1 && !!this.productId);
    }

    private checkInitialLoadComplete() {
        // Skeleton clears only when all three initial calls have resolved: the two stock lists
        // AND the product-details / quantity call (which provides the container cards). Without
        // gating on getQuantity, the skeleton clears before containers are known and the container
        // area paints in late — which is exactly what we want the skeleton to cover.
        if (!this.isUnassignedStockLoading && !this.isStockOnHandLoading && !this.isQuantityLoading) {
            this.isInitialLoad = false;
        }
    }

    get shouldShowSkeleton(): boolean {
        // Summary mode never shows the skeleton (no fetch lifecycle).
        if (this.isSummary) return false;
        // Belt-and-suspenders: keep the skeleton up as long as the initial fetch is in flight OR
        // the bound stock / quantity state has not been assigned yet. The loading flag and the
        // data-presence checks together guarantee the panel never renders against a half-set state.
        return this.isInitialLoad
            || this.isQuantityLoading
            || this.unassignedStock === undefined
            || this.stockOnHand === undefined;
    }

    get skeletonContainerPlaceholders(): number[] {
        // Prefer the known container count (from the parent or from a resolved getQuantity call).
        // While the count is still unknown, fall back to a single placeholder so the skeleton hints
        // at potential container cards. Once getQuantity resolves we trust the real count (which may
        // be 0) to avoid layout shift when the skeleton swaps out.
        const knownCount = this.containers?.length || 0;
        const count = knownCount > 0
            ? knownCount
            : (this.isQuantityLoading ? 1 : 0);
        return Array.from({length: count}, (_, i) => i);
    }

    get skeletonSelectedCard(): 'Unassigned Stock' | 'Stock On Hand' | 'container' | null {
        // Mirror the selection rule used by the populated template so the highlighted card
        // does not shift when real content swaps in.
        if (this.isReadOnly) return null;
        if (this.isDashboard && this.activeTab != 2) return null;
        if (this.stock_available) return 'Stock On Hand';
        if (this.containerId) return 'container';
        return 'Unassigned Stock';
    }

    getStock(type?: any, container?: any) {
        this.requestParamModel = new RequestParamModel();
        this.requestParamModel.expands = ['quote', 'orderCreatedActivity', 'task.task_category', 'task.backend_user', 'product.unit_of_measurement', 'quote_schedule_delivery_product'];
        // Stock On Hand keeps rows for orders already despatched from the warehouse that Unleashed is
        // still holding (not yet dispatched on an Unleashed shipment) — their quantity is still held
        // against available stock, so they are badged "Despatched" rather than shown as a normal
        // pending allocation.
        // The API decides this (same reconciliation that produces Allocated Qty), so the badge can
        // never disagree with the numbers; the other two cards never contain such rows.
        if (type === 'on_hand_stock') this.requestParamModel.expands.push('isDispatchedPending');
        this.requestParamModel.fields = ['id', 'quote_id', 'product_id', 'quantity', 'unit_of_measurement_id', 'quote.order_number','quote.type', 'quote.stock_required_date', 'quote.id', 'quote.is_split_delivery', 'note', 'price', 'discount', 'discounted_price', 'product.unit_of_measurement.name', 'task.task_category.title', 'task.task_number', 'task.task_category.action', 'task.backend_user.first_name', 'task.backend_user.last_name', 'quote_schedule_delivery_product.stock_required_date', 'product.moq', 'product.moi', 'product.is_assembled_product'];

        this.requestParamModel.addDefaultFilters('product_id', this.productId || this.quoteProduct.id, 'number')
        if (type) this.requestParamModel.addDefaultFilters('display_for', type, 'number')
        else this.requestParamModel.addDefaultFilters('container_id', container.id, 'number')
        this.requestParamModel.pageSize = 'all';
        this.quoteService.getQuoteProducts(this.requestParamModel).pipe(takeUntil(this.destroy$)).subscribe((dataList: any) => {
            if (dataList && dataList.data && dataList.data.length > 0) {
                if (type) type == 'unassigned_stock' ? this.unassignedStock = dataList.data : this.stockOnHand = dataList.data;
                else {
                    container['products'] = dataList.data;
                    // Calculate container quantities after loading products
                    this.calculateContainerQuantities(container);
                }
            } else {
                if (type) type == 'unassigned_stock' ? this.unassignedStock = [] : this.stockOnHand = [];
                else {
                    container['products'] = [];
                    // Calculate container quantities even for empty containers
                    this.calculateContainerQuantities(container);
                }
            }
            // ✅ Always set loading to false after data loads
            if (type) {
                type == 'unassigned_stock' ? this.isUnassignedStockLoading = false : this.isStockOnHandLoading = false;
                this.checkInitialLoadComplete();
            }
        }, (error: HttpErrorResponse) => {
            // Assign empty state on error so the skeleton can clear and the existing "No Product Added"
            // empty state renders. Order matters: assign data first, *then* flip the loading flag.
            if (type == 'unassigned_stock') this.unassignedStock = [];
            else if (type == 'on_hand_stock') this.stockOnHand = [];
            else if (container) {
                container['products'] = [];
                this.calculateContainerQuantities(container);
            }
            if (type) {
                type == 'unassigned_stock' ? this.isUnassignedStockLoading = false : this.isStockOnHandLoading = false;
                this.checkInitialLoadComplete();
            }
        });
    }

    /**
     * A closed order line keeps the APCD / ETA it was closed with (the API freezes them on the
     * line). The cards are the product's containers with their LIVE dates, so a card takes the
     * frozen dates instead: from this line when it is the line's own container, otherwise from
     * whichever closed line of the same order sits on it. Every line's panel lists ALL of the
     * product's containers — the parent row of a split, or a split row on another PO, shows the
     * card too, and showed the live date there once a supplier lead-time change moved the PO.
     * A card no closed line of this order sits on is left as it is.
     */
    private withLineDates(containers: any[]): any[] {
        if (!Array.isArray(containers)) return containers;
        const own = this.lineContainer && this.containerId ? this.lineContainer : null;
        const order = this.orderFrozenContainers || {};
        if (!own && !Object.keys(order).length) return containers;
        return containers.map((container: any) => {
            const id = Number(container?.id);
            const frozen = own && id === Number(this.containerId) ? own : order[id];
            return frozen
                ? {
                    ...container,
                    anticipated_production_completion_date: frozen.anticipated_production_completion_date,
                    anticipated_warehouse_arrival_date: frozen.anticipated_warehouse_arrival_date,
                }
                : container;
        });
    }

    getQuantity() {
        this.quantityData = null;
        const requestParamModel = new RequestParamModel();
        requestParamModel.expand = ['unAssignedStock', 'onHandStock', 'pendingTasks.taskCategory', 'pendingTasks.assignedTo', 'containers'];
        requestParamModel.fields = ['id', 'pendingTasks.taskCategory.title', 'pendingTasks.task_number', 'pendingTasks.taskCategory.action', 'pendingTasks.assignedTo.first_name', 'pendingTasks.assignedTo.last_name'];
        delete requestParamModel.pageSize;
        this.productServices.getProductDetails(this.productId || this.quoteProduct.id, requestParamModel).pipe(takeUntil(this.destroy$)).subscribe((dataList: ProductModel) => {
            this.quantityData = dataList;
            if (this.containers?.length == 0 && this.isProductTable) {
                this.containers = this.withLineDates(this.quantityData?.containers);
                this.containers?.forEach((container) => {
                    this.getStock(null, container);
                })
            }
            if (this.productId && this.isDashboard) this.currentQuantityDataEvent.emit(this.quantityData);
            this.isQuantityLoading = false;
            this.checkInitialLoadComplete();
        }, (error: HttpErrorResponse) => {
            this.isQuantityLoading = false;
            this.checkInitialLoadComplete();
        });
    }

    handleAddContainerSidebarClick(container?: any, containerIndex?: any, isMergeContainer?: any) {
        this.isLoading = true;
        if (container) {
            // The same request the Container Management tab makes (see getContainerForEdit).
            this.stockAllocationService.getContainerForEdit(container.id).pipe(takeUntil(this.destroy$)).subscribe(data => {
                if (isMergeContainer) data.isMergeContainer = true;
                this.selectedContainerEvent.emit({container: data, index: containerIndex});
                if (this.quoteProduct || !this.isDashboard) this.selectedProductAddContainerEvent.emit({
                    productId: this.quoteProduct?.id || this.productId,
                    index: this.activeIndex
                });
                this.containerSidebarClickEvent.emit();
                this.isLoading = false;
            }, (error: HttpErrorResponse) => {
                // Was silent: the loader cleared and the form simply never opened.
                this.isLoading = false;
                this.alertService.error(extractErrorMessage(error));
            })
        } else {
            // Clear the host's selection first: it only ever cleared on the form's close button, so
            // after a save "Add New Container" reopened the last container in edit mode.
            this.selectedContainerEvent.emit({container: null, index: containerIndex});
            if (this.quoteProduct || !this.isDashboard) this.selectedProductAddContainerEvent.emit({
                productId: this.quoteProduct?.id || this.productId,
                index: this.activeIndex
            });
            this.containerSidebarClickEvent.emit();
            this.isLoading = false;
        }
    }

    onMouseEnter(title: any) {
        if (this.selectedCard && this.selectedCard != title) {
            this.isdragging = title
        }
    }

    onMouseLeave(title: any) {
        this.selectedCard != title ? this.isdragging = null : ''
    }

    /**
     * Stops a press on a despatched (locked) row from starting a drag of the enclosing product line.
     * Must return nothing: Angular calls preventDefault() on a listener that returns false, which
     * would cancel the press on every unlocked row too.
     */
    blockLockedRowPress(event: MouseEvent, data: any): void {
        if (data?.isDispatchedPending) event.preventDefault();
    }

    onDragStart(event: any, selectedCard: any, data?: any) {
        this.selectedCard = selectedCard;
        this.draggedData = data; // Set dragged data for custom drag preview
        this.stockAllocationService.setDraggedProduct(this.activeIndex);

        // Fix for horizontal scroll drag preview issue - manually set drag image
        if (event.dataTransfer && data) {
            const preview = document.getElementById('customDragPreview');
            if (preview) {
                // Helper function to format date as DD/MM/YYYY
                const formatDate = (dateStr: string): string => {
                    if (!dateStr) return '-';
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return '-';
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                };

                // Update preview content
                const orderDateSpan = preview.querySelector('.drag-preview-order-date');
                const orderSpan = preview.querySelector('.drag-preview-order');
                const qtySpan = preview.querySelector('.drag-preview-qty');
                const stockRequiredDateSpan = preview.querySelector('.drag-preview-stock-required-date');

                if (orderDateSpan) orderDateSpan.textContent = formatDate(data?.quote?.orderCreatedActivity?.created_at);
                if (orderSpan) orderSpan.textContent = data?.quote?.order_number || '';
                if (qtySpan) {
                    const uom = data?.product?.unit_of_measurement?.name;
                    const uomDisplay = uom === this.constants.UNIT_OF_MEASUREMENT_SQM ? 'm²' : (uom?.toLowerCase() || '');
                    qtySpan.textContent = `${data?.quantity || ''} ${uomDisplay}`;
                }
                if (stockRequiredDateSpan) stockRequiredDateSpan.textContent = data?.quote_schedule_delivery_product?.stock_required_date ? formatDate(data?.quote_schedule_delivery_product?.stock_required_date) : '-';

                // Temporarily make preview visible for setDragImage
                preview.style.top = '-500px';
                preview.style.left = '0';

                // Set the custom element as drag image
                event.dataTransfer.setDragImage(preview, 0, 0);
            }
        }
    }

    calculateModulo(dividend: number, divisor: number): number {
        if (!dividend || !divisor || divisor === 0) return 0;
        const ratio = dividend / divisor;
        const rounded = Math.round(ratio);
        return Math.abs(ratio - rounded) < 1e-9 ? 0 : dividend % divisor;
    }

    showMoiWarning(quantity: number, moi: number, uom: string): Promise<boolean> {
        return new Promise((resolve) => {
            const instance = this.modalService.open(MoiWarningModalComponent, {
                backdrop: 'static',
                keyboard: false,
                windowClass: 'donationModals quotes-model quote-order-export-pdf-model moi-warning-modal'
            });
            instance.componentInstance.quantity = quantity;
            instance.componentInstance.moi = moi;
            instance.componentInstance.uom = uom || '';
            instance.result.then(() => resolve(true)).catch(() => resolve(false));
        });
    }

    async onDrop(event: any, targetArray?: any, selectedCard?: any, container?: any) {
        if (this.selectedCard == selectedCard) return;
        // Ignore a second drop while one is still in flight. The loader covers this component, but a
        // mid-request teardown/rebuild resets it, briefly leaving the cards droppable again. The
        // server is the real guard (splits are serialised per order and the quantity is re-read
        // inside that lock, so an over-split is rejected) — this just stops the pointless attempt.
        if (this.isLoading) return;
        let data: any = event.data.data;
        if (!data) return;

        // Unit mode moves assembled units; component mode moves one component's slices.
        if (this.assembledUnitBoard) {
            await this.dropAssembledUnits(data, selectedCard, container);
            return;
        }
        if (this.componentAllocation) {
            await this.dropComponent(data, selectedCard, container);
            return;
        }

        /*
         * A row with no order-line id is a component slice, so this card set should have been in
         * component mode and is not. Refuse rather than PATCH /quote-product/undefined, which the
         * API answers with a 500 that reads as a server fault rather than the wiring mistake it is.
         */
        if (!data.id) {
            this.alertService.error('That row cannot be moved from here - reopen the component and try again.');
            return;
        }
        let availableQty: any;
        if (selectedCard === 'Stock On Hand') {
            availableQty = this.quantityData?.onHandStock?.available_qty;
        } else if (container) {
            availableQty = container?.available_qty;
        }
        let transfer: any = await this.transfer(data, selectedCard, availableQty);
        if (!transfer) return;

        let qty = +transfer.splitStock || +transfer.quantity;

        const moi = +data?.product?.moi;
        if (moi > 0 && selectedCard !== 'Unassigned Stock' && this.calculateModulo(qty, moi) !== 0) {
            const uom = data?.product?.unit_of_measurement?.name || this.uom || '';
            const confirmed = await this.showMoiWarning(qty, moi, uom);
            if (!confirmed) return;
        }
        let model: any = {id: data.id}
        switch (selectedCard) {
            case 'Stock On Hand':
                // if (qty > this.quantityData?.onHandStock?.available_qty) {
                //   this.alertService.error(this.constants.STOCK_ALLOCATION_CONSTANTS.LESS_AVAILABLE_QTY_ERROR);
                //   return;
                // }
                model.stock_available = true;
                if (this.selectedCard != 'Unassigned Stock') model.container_id = null;
                break;
            case container?.id:
                // // When moving TO a container, validate the SOURCE quantity, not the target container
                // if (this.selectedCard == 'Stock On Hand' && qty > this.quantityData?.onHandStock?.available_qty) {
                //   this.alertService.error(this.constants.STOCK_ALLOCATION_CONSTANTS.LESS_AVAILABLE_QTY_ERROR);
                //   return;
                // }
                // For container to container moves, validate source container
                if (this.selectedCard !== 'Stock On Hand' && this.selectedCard !== 'Unassigned Stock') {
                    const sourceContainerIndex = this.containers.findIndex(c => c.id === this.selectedCard);
                    // if (sourceContainerIndex >= 0 && qty > this.containers[sourceContainerIndex]?.available_qty) {
                    //   this.alertService.error(this.constants.STOCK_ALLOCATION_CONSTANTS.LESS_AVAILABLE_QTY_ERROR);
                    //   return;
                    // }
                }
                // No validation needed when moving FROM unassigned stock TO container
                model.container_id = container.id;
                if (this.selectedCard == 'Stock On Hand') model.stock_available = false;
                break;
            case 'Unassigned Stock':
                if (this.selectedCard == 'Stock On Hand')
                    model.stock_available = false;
                else
                    model.container_id = null;
                break;
            default:
                break;
        }
        this.isLoading = true;
        if (transfer.splitStock) {
            // ✅ ATOMIC SPLIT: one server call creates the child AND reduces the parent, then
            // pushes to Unleashed and verifies the order matches. If the push/verify does not
            // complete, the server compensates (reverts) so the order is left unchanged. This
            // replaces the old two-call chain whose second call (reduce parent) could be silently
            // dropped when this component was torn down by a socket refresh mid-request.
            this.quoteService.splitProductLine({
                parent_id: data.id,
                split_quantity: qty,
                stock_available: model.stock_available === true,
                container_id: model.container_id ?? null,
            })
            // ⚠️ DELIBERATELY NOT takeUntil(this.destroy$).
            //
            // This component lives inside `@if (... productsRow.isStockExpanded ...)` in the order's
            // product table, so it is destroyed and rebuilt every time the order refreshes — which a
            // socket event from any source can do at any moment. A split takes seconds (it waits on
            // Unleashed), so a teardown lands mid-request often enough to matter, and takeUntil then
            // dropped this whole handler: the split had SUCCEEDED on the server but nothing on screen
            // changed. The admin then saw the original quantity and split it a second time.
            //
            // The server is already atomic, so letting the response land cannot corrupt anything; the
            // worst case is a refresh triggered by a component nobody is looking at. The refreshes
            // below go through the root-scoped subscriber service precisely so they still reach the
            // live components after this instance is gone.
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    // Three independent refreshes, because each covers a case the others do not:
                    //  1. refreshStockData()  — these cards, when this instance is still alive.
                    //  2. triggerQuote()      — root-scoped, so it reaches the order's product table
                    //                           (and any other live stock panel) even if this
                    //                           instance was destroyed while the request was open.
                    //  3. dropActionCompleted — the parent's own refresh path.
                    // updateStockAllocationByProduct is NOT a refresh; it only drives a header badge.
                    this.refreshStockData();
                    this.subscribersService.triggerQuote();
                    this.subscribersService.updateStockAllocationByProduct(true);
                    if (this.activeTab == 2) {
                        this.orderDetailRefresh.emit(true);
                    }
                    this.alertService.success(this.constants.STOCK_ALLOCATION_CONSTANTS.PRODUCT_DRAG_DROP);
                    if (this.isDashboard && this.quantityData) {
                        this.currentQuantityDataEvent.emit({
                            containers: this.containers,
                            onHandStock: this.quantityData.onHandStock,
                            activeIndex: this.activeIndex
                        });
                    }
                    // Emit event to notify parent about drop action completion
                    this.dropActionCompleted.emit({
                        quoteId: data.quote_id,
                        productId: data.product_id,
                        orderId: this.orderId
                    });
                    this.webSocketService.emit('quote_updated', {
                        quoteId: this.orderId,
                        exportId: this.exportId
                    });
                    this.webSocketService.emit('order_created', {
                        quoteId: this.orderId,
                        exportId: this.exportId,
                        userId: this.frontendUserId
                    });
                },
                error: (error: HttpErrorResponse) => {
                    console.error('❌ splitProductLine API failed:', error);
                    this.isLoading = false;
                    this.sourceArray = [];
                    this.sourceIndex = -1;
                    // Server guarantees the order is unchanged on failure — refresh to the true state
                    // through the same root-scoped path the success branch uses, so a failed split
                    // cannot leave a stale screen either.
                    this.refreshStockData();
                    this.subscribersService.triggerQuote();
                    this.subscribersService.updateStockAllocationByProduct(true);
                    this.alertService.error(error?.error?.message || 'Split failed - order left unchanged.');
                },
            });
        } else {
            this.quoteService.updateQuoteProductForCreateClone(model, model.id).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
                data.quantity = qty;

                // Ensure container products array is initialized when moving to a container
                if (container && selectedCard === container.id) {
                    if (!container.products) {
                        container.products = [];
                    }
                    container.products.push(data);
                } else {
                    targetArray?.push(data);
                }

                this.modifyTargetQuantityData(container, qty, selectedCard);
                this.sourceArray.splice(this.sourceIndex, 1);
                this.modifySourceQuantityData(event.data?.containerIndex, qty);

                // ⚠️ FIX: Call splitProduction API and wait for completion before triggering refresh
                this.debugLogger.log('🔄 Calling splitProduction API...');
                if (data?.product) {
                    this.quoteService.splitProduction({id: data.quote_id}).pipe(takeUntil(this.destroy$)).subscribe({
                        next: () => {
                            this.debugLogger.log('✅ splitProduction API completed successfully');
                            this.isLoading = false;
                            this.subscribersService.updateStockAllocationByProduct(true);
                            if (this.activeTab == 2) {
                                this.orderDetailRefresh.emit(true);
                            }
                            // ⚠️ NOTE: triggerQuote() removed - the order_created socket event
                            // triggers refresh via WebSocket subscription in order-detail component

                            this.alertService.success(this.constants.STOCK_ALLOCATION_CONSTANTS.PRODUCT_DRAG_DROP);
                            if (this.isDashboard && this.quantityData) {
                                this.currentQuantityDataEvent.emit({
                                    containers: this.containers,
                                    onHandStock: this.quantityData.onHandStock,
                                    activeIndex: this.activeIndex
                                });
                            }

                            // Emit event to notify parent about drop action completion
                            this.dropActionCompleted.emit({
                                quoteId: data.quote_id,
                                productId: data.product_id,
                                orderId: this.orderId
                            });
                            this.webSocketService.emit('quote_updated', {
                                quoteId: this.orderId,
                                exportId: this.exportId
                            });
                            this.webSocketService.emit('order_created', {
                                quoteId: this.orderId,
                                exportId: this.exportId,
                                userId: this.frontendUserId
                            });
                            this.debugLogger.log('✅ Drop action completed - emitting to parent', {
                                quoteId: data.quote_id,
                                orderId: this.orderId
                            });
                        },
                        error: (error: HttpErrorResponse) => {
                            console.error('❌ splitProduction API failed:', error);
                            this.isLoading = false;
                            this.sourceArray = [];
                            this.sourceIndex = -1;
                            this.alertService.error('Something went wrong during split production.');
                        },
                    });
                }
            }, (error) => {
                this.sourceArray = [];
                this.sourceIndex = -1;
                this.isLoading = false;
                this.alertService.error('Something went wrong while moving the product.');
            })
        }
        // this.selectedCard = selectedCard;
    }

    modifyTargetQuantityData(container: any, qty: any, selectedCard: any) {
        if (container) {
            // container.total_qty += qty;
            container.allocated_qty = +(container.allocated_qty + qty).toFixed(2);
            container.available_qty = +(container.total_qty - container.allocated_qty).toFixed(2);
        } else if (selectedCard == 'Stock On Hand') {
            // ⚠️ FIX: Add null check for quantityData
            if (this.quantityData?.onHandStock) {
                // this.quantityData.onHandStock.total_qty += qty;
                this.quantityData.onHandStock.allocated_qty = +(this.quantityData.onHandStock.allocated_qty + qty).toFixed(2);
                this.quantityData.onHandStock.available_qty = +(this.quantityData.onHandStock.total_qty - this.quantityData.onHandStock.allocated_qty).toFixed(2);
            }
        } else if (selectedCard == 'Unassigned Stock') {
            // ⚠️ FIX: Add null check for quantityData
            if (this.quantityData?.unAssignedStock) {
                this.quantityData.unAssignedStock.total_qty = +(this.quantityData.unAssignedStock.total_qty + qty).toFixed(2);
            }
        }
    }

    modifySourceQuantityData(containerIndex: any, qty: any) {
        this.sourceArray = [];
        this.sourceIndex = -1;

        switch (this.selectedCard) {
            case 'Stock On Hand':
                // ⚠️ FIX: Add null check for quantityData
                if (this.quantityData?.onHandStock) {
                    // this.quantityData.onHandStock.total_qty -= qty;
                    this.quantityData.onHandStock.allocated_qty = +(this.quantityData.onHandStock.allocated_qty - qty).toFixed(2);
                    this.quantityData.onHandStock.available_qty = +(this.quantityData.onHandStock.total_qty - this.quantityData.onHandStock.allocated_qty).toFixed(2);
                }
                break;
            case 'Unassigned Stock':
                // ⚠️ FIX: Add null check for quantityData
                if (this.quantityData?.unAssignedStock) {
                    this.quantityData.unAssignedStock.total_qty = +(this.quantityData.unAssignedStock.total_qty - qty).toFixed(2);
                }
                break;
            default:
                if (this.containers[containerIndex]) {
                    this.containers[containerIndex].allocated_qty = +(this.containers[containerIndex].allocated_qty - qty).toFixed(2);
                    this.containers[containerIndex].available_qty = +(this.containers[containerIndex].total_qty - this.containers[containerIndex].allocated_qty).toFixed(2);
                }
                break;
        }
    }

    onDragEnd(event: DragEvent, sourceArray: any, index: any, selectedCard: any) {
        this.isdragging = null;
        this.sourceArray = sourceArray;
        this.sourceIndex = index;
        this.draggedData = null; // Clear dragged data when drag ends
        // if (this.selectedCard == selectedCard) {
        //   this.selectedCard = null;
        //   return;
        // }
        // if (index !== -1) {
        //   sourceArray.splice(index, 1);
        // }
        // this.selectedCard = null;
    }

    addComment(product: any) {
        const instance = this.modalService.open(AddCommentComponent);
        instance.componentInstance.commentModel = product;
        instance.componentInstance.addCommentStockAllocation = true;
        instance.closed.subscribe((result) => {
            if (result) {
                this.webSocketService.emit('quote_updated', {
                    quoteId: this.orderId,
                    exportId: null
                });
                this.webSocketService.emit('order_created', {
                    quoteId: this.orderId,
                    exportId: null
                });
            }
        });
    }

    mergeProduct(index: number, container: any) {
        // let modalRef: any = this.bsModalService.show(ConfirmModalComponent);
        // modalRef.content.title = 'Merge Container';
        // modalRef.content.message = Constants.STOCK_ALLOCATION_CONSTANTS.MERGE_CONFIRM;
        // modalRef.content.note = Constants.STOCK_ALLOCATION_CONSTANTS.MERGE_CONFIRM_NOTE;
        // modalRef.content.onClose.subscribe((result) => {
        //   if (result) {
        this.isLoading = true;
        this.stockAllocationService.mergeContainer({id: container.id}).pipe(takeUntil(this.destroy$)).subscribe((res) => {
            this.containers.splice(index, 1);
            if (container?.products) {
                this.stockOnHand = [...this.stockOnHand, ...container?.products];
            }
            if (container?.quote_product) {
                this.stockOnHand = [...this.stockOnHand, ...container?.quote_product];
                container.allocated_qty = container?.quote_product?.reduce(
                    (sum: number, item: any) => +sum + (+item.quantity || 0),
                    0
                ) || 0;
            }
            // this.quantityData.onHandStock.total_qty = +(this.quantityData.onHandStock.total_qty + container.total_qty).toFixed(2);
            this.quantityData.onHandStock.allocated_qty = +(this.quantityData.onHandStock.allocated_qty + container.allocated_qty).toFixed(2);
            this.quantityData.onHandStock.available_qty = +(this.quantityData.onHandStock.total_qty - this.quantityData.onHandStock.allocated_qty).toFixed(2);
            this.isLoading = false;
            this.alertService.success(this.constants.STOCK_ALLOCATION_CONSTANTS.CONTAINER_MERGE);
            if (this.isDashboard) this.currentQuantityDataEvent.emit({
                containers: this.containers,
                onHandStock: this.quantityData.onHandStock,
                activeIndex: this.activeIndex
            });
            this.subscribersService.mergeContainer.set(null)
        }, (error) => {
            this.isLoading = false;
            this.alertService.error('Something went wrong while merging the container.');
            this.subscribersService.mergeContainer.set(null)
        })
        //   }
        // })
    }

    onSelectFilterChange(event) {
    }

    /* ############ Assembled products — UNIT mode ############ */

    /**
     * Fills the ordinary card variables from the assembled board, so the existing template draws it
     * with no assembled-specific branch. The figures are assembled UNITS; each card is kept whole in
     * unitCards for its tooltip.
     *
     * `silent`: a background refresh (live stock event) — no skeleton, no error toast.
     */
    loadAssembledUnitBoard(silent = false): void {
        const quoteProductId = this.assembledUnitBoard?.quoteProductId;
        const productId = this.assembledUnitBoard?.productId;
        const hasLine = quoteProductId !== null && quoteProductId !== undefined;
        if (!hasLine && !productId) {
            return;
        }

        if (!silent) this.startInitialLoad();
        // An order line's board, or the product's when no line is in view (By Product) — same cards.
        const board$ = hasLine
            ? this.stockAllocationService.getAssembledUnitBoard(quoteProductId)
            : this.stockAllocationService.getAssembledProductBoard(productId);
        board$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (board: any) => {
                    this.uom = board?.product?.uom ?? this.uom;
                    this.unitBoardBlockedReason = board?.is_blocked
                        ? (board?.blocked_reason || 'This line cannot be allocated yet.')
                        : null;
                    const cards: any[] = board?.cards ?? [];

                    /*
                     * Each card whole, for its (i): the card's own arithmetic, so the tooltip can show
                     * its working rather than assert a number. The shelf follows Unleashed's own Can
                     * Assemble — `unleashed` says where those figures came from.
                     */
                    this.unitCards = {};
                    cards.forEach((card: any) => {
                        this.unitCards[card.key] = card;
                    });
                    this.unitBoardUnleashed = board?.unleashed ?? null;
                    this.unitBoardProductId = Number(board?.product?.id) || Number(productId) || null;
                    this.watchAssembledStock();

                    const unassigned = cards.find((c: any) => c.key === 'UNASSIGNED');
                    const warehouse = cards.find((c: any) => c.key === 'WAREHOUSE');

                    this.unassignedStock = unassigned?.rows ?? [];
                    this.stockOnHand = warehouse?.rows ?? [];

                    this.containers = cards
                        .filter((c: any) => c.source_type === 'CONTAINER')
                        .map((c: any) => StockAllocationContentComponent.containerFromUnitCard(c));

                    this.quantityData = {
                        unAssignedStock: {total_qty: unassigned?.total_qty ?? 0},
                        onHandStock: {
                            total_qty: warehouse?.total_qty ?? 0,
                            allocated_qty: warehouse?.allocated_qty ?? 0,
                            available_qty: warehouse?.available_qty ?? 0,
                            // Said outright: the shelf's Available never drops below 0 (as in
                            // Unleashed), so Total < Allocated can no longer signal it.
                            is_overallocated: warehouse?.is_overallocated,
                        },
                        pendingTasks: [],
                    };

                    /*
                     * By Product: the page's hover beside the selected product (Stock On Hand,
                     * Allocated, Available, On Purchase) reads the figures its card set reports, as
                     * it does for an ordinary product. Without this it showed whichever product's
                     * cards reported last.
                     */
                    if (this.isDashboard) {
                        this.currentQuantityDataEvent.emit({
                            containers: this.containers,
                            onHandStock: this.quantityData.onHandStock,
                            activeIndex: this.activeIndex
                        });
                    }

                    this.isUnassignedStockLoading = false;
                    this.isStockOnHandLoading = false;
                    this.isQuantityLoading = false;
                    this.isInitialLoad = false;
                    this.isLoading = false;
                    this.changeDetection.detectChanges();
                },
                error: (err: any) => {
                    // A failed background refresh keeps the cards already on screen.
                    if (silent) return;
                    this.isUnassignedStockLoading = false;
                    this.isStockOnHandLoading = false;
                    this.isQuantityLoading = false;
                    this.isInitialLoad = false;
                    this.isLoading = false;
                    this.unitBoardBlockedReason = null;
                    this.alertService.error(err?.error?.message || 'Could not load the allocation cards.');
                    this.changeDetection.detectChanges();
                }
            });
    }

    /**
     * Unit mode: re-reads the board when an order holding this product's parts reaches, changes in or
     * is cancelled against Unleashed — another order may have taken (or freed) the shelf this board
     * shows. Ignored mid-drag or mid-move: that action reloads the board itself.
     */
    private watchAssembledStock(): void {
        if (this.watchingAssembledStock) return;
        this.watchingAssembledStock = true;
        this.webSocketService.joinRoom('assembled_stock');
        this.webSocketService.getAssembledStockEvents()
            .pipe(
                filter((event) => !!this.unitBoardProductId
                    && (event?.product_ids ?? []).some((id) => Number(id) === this.unitBoardProductId)),
                debounceTime(1500),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.isLoading || this.isdragging || !this.assembledUnitBoard) return;
                this.loadAssembledUnitBoard(true);
            });
    }

    /**
     * Splits N units off a line into a child line and allocates that child to the chosen card.
     *
     * The order-line split is the ordinary endpoint, so an assembled line splits exactly as any
     * other does. Two follow-ups are assembled-specific: the parent is trimmed, because the split
     * leaves it holding components for the quantity it used to have; and the child is allocated to
     * the card the units were dropped on, because a freshly split line starts with nothing.
     */
    private async splitAssembledUnitsToCard(lineId: number, units: number, target: any, container: any, data: any, sourceKey: string): Promise<void> {
        this.isLoading = true;

        this.quoteService.splitProductLine({
            parent_id: lineId,
            split_quantity: units,
            stock_available: false,
            container_id: null,
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
                /*
                 * child_id, NOT id: the endpoint returns the updated PARENT line, so `id` is the line
                 * we split FROM. Allocating that would re-fill the parent and leave the new line
                 * empty — which is precisely what this did before the split was audited.
                 */
                const childId = res?.child_id ?? res?.data?.child_id ?? null;

                /*
                 * The parent now needs less than it holds. Release the difference FROM THE CARD THE
                 * SPLIT WAS DRAGGED OFF (`prefer`): a plain rebalance trims whichever slice was placed
                 * last, so the split could leave that card untouched. Unassigned holds nothing to
                 * release, so it has no preference.
                 *
                 * This used to be a moveAssembledUnits() off that card first. That checks the units
                 * against the parent's NEW quantity, so moving 1.7 of a 2.55 line refused with "Only
                 * 0.85 of this order is on that card" beside the split's own success message
                 * (ClickUp 14yjutqf3cf).
                 */
                this.stockAllocationService.rebalanceComponentAllocations(lineId, sourceKey !== 'UNASSIGNED' ? sourceKey : undefined)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => this.placeSplitChild(childId, units, target, container, data, sourceKey),
                        error: () => this.placeSplitChild(childId, units, target, container, data, sourceKey),
                    });
            },
            error: (err: any) => {
                this.isLoading = false;
                // The server guarantees the order is unchanged on failure — refresh to the truth.
                this.subscribersService.updateStockAllocationByProduct(true);
                this.alertService.error(err?.error?.message || 'Split failed - order left unchanged.');
                this.loadAssembledUnitBoard();
            }
        });
    }

    /**
     * Allocates a freshly split child line to the card its units were dropped on — never back onto
     * the card they were split off (`avoid`), which has just been freed and so would otherwise take
     * them first whenever it arrives before the target.
     */
    private placeSplitChild(childId: any, units: number, target: any, container: any, data: any, sourceKey?: string): void {
        if (!childId) {
            // Split succeeded but its id did not come back; the new line is simply unallocated.
            this.isLoading = false;
            this.alertService.success(units + ' split onto a new line - allocate it from Unassigned.');
            this.afterAssembledChange(data?.quote_product_id, data, true);
            return;
        }

        this.stockAllocationService.moveAssembledUnits(childId, {
            from: {key: 'UNASSIGNED'},
            to: {key: this.unitCardKey(target, container)},
            units,
            ...(sourceKey && sourceKey !== 'UNASSIGNED' ? {avoid: [sourceKey]} : {}),
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.isLoading = false;
                this.alertService.success(units + ' split onto a new line and allocated.');
                this.afterAssembledChange(childId, data, true);
            },
            error: (err: any) => {
                this.isLoading = false;
                this.alertService.error(
                    err?.error?.message || 'Split, but the new line could not be allocated - it is in Unassigned.'
                );
                this.loadAssembledUnitBoard();
            }
        });
    }

    /**
     * The housekeeping an ordinary product's drop does, which an assembled one must do too: tell the
     * other views the order changed, refresh the order screen when this panel is embedded in it, and
     * let other users' screens know over the socket. Without these the change is correct in the
     * database but stale everywhere except this panel.
     */
    private afterAssembledChange(lineId: any, data: any, structural: boolean = false): void {
        this.loadAssembledUnitBoard();

        /*
         * `structural` means the order's SHAPE changed — a split added a line, a merge removed one.
         * Those have to reach the order screen, or the new line simply is not there until someone
         * reloads the page. So the order is re-fetched, and the panel survives it because
         * ProductDetailSection re-hydrates any assembled row that is still expanded.
         *
         * A plain move changes no shape, only which source holds what. The board has already been
         * refreshed above and the order row is updated in place, so re-fetching the whole order
         * would only cost a flicker.
         */
        if (structural || this.activeTab == 2) {
            this.subscribersService.updateStockAllocationByProduct(true);
            this.orderDetailRefresh.emit(true);
        }
        /*
         * `structural` travels with the event because the order screen has to re-fetch the order to
         * show a line that has just appeared or gone. It goes through dropActionCompleted rather
         * than orderDetailRefresh: the order-details embed does not bind orderDetailRefresh at all,
         * so emitting it there reaches nobody.
         */
        this.dropActionCompleted.emit({
            quoteProductId: lineId,
            quoteId: data?.quote?.id,
            orderId: this.orderId,
            structural,
        });
        /*
         * Also gated to the Stock Allocation tabs.
         *
         * The socket broadcast comes back to this browser too, and the order screen reloads the whole
         * order when it arrives — which replaces every product row and takes this panel's state with
         * it. So on the order-details embed the notification would shut the panel the person is
         * working in, to tell them about a change they just made themselves.
         *
         * The cost is that a colleague with the same order open is not live-notified of an assembled
         * allocation made from the order screen; they see it on their next load. Fixing that properly
         * means making the panel survive an order reload rather than suppressing the notice.
         */
        if (structural || this.activeTab == 2) {
            this.webSocketService.emit('quote_updated', {
                quoteId: this.orderId,
                exportId: this.exportId,
            });
            this.webSocketService.emit('order_created', {
                quoteId: this.orderId,
                exportId: this.exportId,
                userId: this.frontendUserId,
            });
        }
    }

    /** The board key for a card, from what the template already passes it. */
    unitCardKey(title: any, container?: any): string {
        if (container) {
            return container.unitCardKey || ('CONTAINER:' + container.id);
        }
        return title === 'Unassigned Stock' ? 'UNASSIGNED' : 'WAREHOUSE';
    }

    /**
     * A container card of the unit board, in the container shape this template reads. Also what the
     * Summary tab's container hover is given for an assembled product, so the two show the same card.
     */
    static containerFromUnitCard(c: any): any {
        return {
            id: c.container_id,
            order_number: c.title,
            status: c.status,
            anticipated_warehouse_arrival_date: c.eta,
            anticipated_production_completion_date: c.anticipated_production_completion_date,
            is_domestic_stock_transfer: c.is_domestic_stock_transfer,
            delay_reasons: c.delay_reasons ?? [],
            total_qty: c.total_qty,
            allocated_qty: c.allocated_qty,
            available_qty: c.available_qty,
            products: c.rows ?? [],
            pendingTasks: [],
            // so the template can reach them without a key lookup
            unitCardKey: c.key,
        };
    }

    /** A card of the unit board, for its (i) — app-assembled-card-info explains the card's figure. */
    unitCard(title: any, container?: any): any {
        return this.unitCards?.[this.unitCardKey(title, container)] ?? null;
    }

    /**
     * The Stock On Hand card's Overallocated badge. The assembled board says so outright, because its
     * Available follows Unleashed and never drops below 0; every other card still compares Total with
     * Allocated, exactly as before.
     */
    isStockOnHandOverallocated(): boolean {
        const onHand = this.quantityData?.onHandStock;
        if (onHand?.is_overallocated !== undefined && onHand?.is_overallocated !== null) {
            return !!onHand.is_overallocated;
        }
        return onHand?.total_qty < onHand?.allocated_qty;
    }

    /**
     * A row dragged between cards, in assembled units.
     *
     * Only this order's own units can be moved from here; another order's row is shown so it is
     * clear who holds the stock, but it is changed from that order.
     */
    async dropAssembledUnits(data: any, selectedCard: any, container?: any): Promise<void> {
        /*
         * The row's OWN line is what moves — not necessarily the line this panel was opened for.
         *
         * The cards list every order drawing on these components, and moving another order's units
         * is the point: freeing what an earlier order is holding is how an urgent later one gets it.
         * It also matches an ordinary product's cards, which let you drag any order's row. The
         * server validates against that line's own figures, not this one's.
         */
        const movingLineId = data?.quote_product_id ?? this.assembledUnitBoard?.quoteProductId;
        if (movingLineId === null || movingLineId === undefined) {
            return;
        }
        const isOtherOrder = !data?.is_this_order;

        const target = container ? container.id : selectedCard;

        let availableQty: any;
        if (selectedCard === 'Stock On Hand') {
            availableQty = this.quantityData?.onHandStock?.available_qty;
        } else if (container) {
            availableQty = container?.available_qty;
        }

        let transfer: any;
        try {
            transfer = await this.transfer(data, selectedCard, availableQty);
        } catch (e) {
            return;
        }
        if (!transfer) {
            return;
        }

        /*
         * Same multiple-of-increment warning an ordinary product gets. It is checked against the
         * ASSEMBLED product's own increment, because that is what is being committed — a quantity of
         * finished product — and it is the assembled line the customer ordered.
         */
        const requested = +transfer.splitStock || +transfer.quantity;
        const moi = +(data?.product?.moi ?? this.assembledUnitBoard?.moi ?? 0);
        if (moi > 0 && selectedCard !== 'Unassigned Stock' && this.calculateModulo(requested, moi) !== 0) {
            const confirmed = await this.showMoiWarning(requested, moi, data?.product?.unit_of_measurement?.name || this.uom || '');
            if (!confirmed) {
                return;
            }
        }

        /*
         * "Split Stock" means the same thing here as it does for an ordinary product: the quantity
         * being moved becomes a LINE OF ITS OWN, with the original as its parent, so the two halves
         * can be delivered separately. "Full Stock" just moves what is there.
         *
         * For an ordinary product a partial move HAS to split, because its line carries one source.
         * An assembled line can hold several, so it does not have to — but making Split Stock mean
         * something different here is exactly the inconsistency this screen exists to avoid.
         */
        // The card the row was dragged OFF — set by onDragStart.
        const sourceKey = this.unitCardKey(this.selectedCard, this.containers?.find((c: any) => c.id === this.selectedCard));

        if (transfer.splitStock) {
            await this.splitAssembledUnitsToCard(movingLineId, +transfer.splitStock, target, container, data, sourceKey);
            return;
        }

        const units = +transfer.quantity;

        this.isLoading = true;
        this.stockAllocationService.moveAssembledUnits(movingLineId, {
            from: {key: sourceKey},
            to: {key: this.unitCardKey(target, container)},
            units,
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
                // Dropped back on the card it came from: the server moves nothing, so neither say
                // "moved" nor touch the order — it used to report success here.
                if (res?.moved === false) {
                    this.isLoading = false;
                    this.alertService.info('Nothing to move - those units are already on that card.');
                    return;
                }
                /*
                 * Re-evaluate the order's split-production flag before refreshing, exactly as the
                 * ordinary path does after a move: units now sitting on different sources can change
                 * whether the order is produced in more than one run, and the screens that read that
                 * flag must not be refreshed until it has been recalculated.
                 *
                 * The quote is taken from the ROW, not from this panel: the units moved may belong to
                 * another order listed on the card.
                 */
                const quoteId = data?.quote?.id ?? this.orderId;
                const done = (message?: string) => {
                    this.isLoading = false;
                    if (message) {
                        this.alertService.error(message);
                    } else {
                        // Say whose stock moved when it was not this order's, so it is never a surprise.
                        this.alertService.success(isOtherOrder
                            ? ('Moved ' + units + ' from ' + (data?.quote?.order_number || 'another order') + '.')
                            : this.constants.STOCK_ALLOCATION_CONSTANTS.PRODUCT_DRAG_DROP);
                    }
                    this.afterAssembledChange(movingLineId, data);
                };

                if (!quoteId) {
                    done();
                    return;
                }

                this.quoteService.splitProduction({id: quoteId}).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => done(),
                    // The move itself succeeded, so the board is still refreshed — only the flag is in doubt.
                    error: () => done('Moved, but something went wrong during split production.'),
                });
            },
            error: (err: any) => {
                this.isLoading = false;
                this.alertService.error(err?.error?.message || 'Could not move those units.');
                this.loadAssembledUnitBoard();
            }
        });
    }

    /* ############ Assembled products — component mode ############ */

    /**
     * Fills the ordinary card variables from one component's board, so the existing template draws
     * it with no component-specific branch: `unassignedStock`, `stockOnHand`, `containers` and the
     * `quantityData` the Total/Allocated/Available boxes read.
     */
    loadComponentBoard(): void {
        const quoteProductId = this.componentAllocation?.quoteProductId;
        const componentProductId = this.componentAllocation?.componentProductId;
        // 0 is legitimate: the By Product tab has no single order line in view, so nothing is
        // flagged as "this order" — every order's slices are still listed. Only a missing value
        // means there is nothing to load.
        if (quoteProductId === null || quoteProductId === undefined || !componentProductId) {
            return;
        }

        this.startInitialLoad();
        this.stockAllocationService.getComponentBoard(quoteProductId, componentProductId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (board: any) => {
                    this.uom = board?.component?.uom ?? this.uom;

                    const cards: any[] = board?.cards ?? [];
                    const unassigned = cards.find((c: any) => c.key === 'UNASSIGNED');
                    const warehouse = cards.find((c: any) => c.key === 'WAREHOUSE');

                    this.unassignedStock = unassigned?.rows ?? [];
                    this.stockOnHand = warehouse?.rows ?? [];

                    // Shaped exactly like the container objects the ordinary path builds, because the
                    // card header reads order_number / status / ETA / APCD straight off them.
                    this.containers = cards
                        .filter((c: any) => c.source_type === 'CONTAINER')
                        .map((c: any) => ({
                            id: c.container_id,
                            order_number: c.title,
                            status: c.status,
                            anticipated_warehouse_arrival_date: c.eta,
                            anticipated_production_completion_date: c.anticipated_production_completion_date,
                            is_domestic_stock_transfer: c.is_domestic_stock_transfer,
                            delay_reasons: c.delay_reasons ?? [],
                            total_qty: c.total_qty,
                            allocated_qty: c.allocated_qty,
                            available_qty: c.available_qty,
                            products: c.rows ?? [],
                            pendingTasks: [],
                        }));

                    this.quantityData = {
                        unAssignedStock: {total_qty: unassigned?.total_qty ?? 0},
                        onHandStock: {
                            total_qty: warehouse?.total_qty ?? 0,
                            allocated_qty: warehouse?.allocated_qty ?? 0,
                            available_qty: warehouse?.available_qty ?? 0,
                        },
                        pendingTasks: [],
                    };

                    // getStock/getQuantity never run here, so clear their flags by hand or the
                    // skeleton never lifts.
                    this.isUnassignedStockLoading = false;
                    this.isStockOnHandLoading = false;
                    this.isQuantityLoading = false;
                    this.isInitialLoad = false;
                    this.isLoading = false;
                    this.changeDetection.detectChanges();
                },
                error: (err: any) => {
                    this.isUnassignedStockLoading = false;
                    this.isStockOnHandLoading = false;
                    this.isQuantityLoading = false;
                    this.isInitialLoad = false;
                    this.isLoading = false;
                    this.alertService.error(err?.error?.message || 'Could not load that component.');
                    this.changeDetection.detectChanges();
                }
            });
    }

    /**
     * Which allocation source a card represents. The template already identifies cards as
     * 'Unassigned Stock', 'Stock On Hand' or a container id, so the drag needs no extra plumbing.
     */
    private componentSourceOf(card: any): any {
        if (card === 'Unassigned Stock') {
            return {source_type: 'TBC', container_id: null};
        }
        if (card === 'Stock On Hand') {
            return {source_type: 'WAREHOUSE', container_id: null};
        }
        return {source_type: 'CONTAINER', container_id: card};
    }

    /**
     * A component row dragged between two cards.
     *
     * Writes a MOVE (add to the destination, subtract from the origin, one transaction) rather than
     * replacing the component's allocation, so a component can end up split across the shelf and one
     * or more containers — §4.2 step 3 — exactly as an ordinary product's line can.
     */
    async dropComponent(data: any, selectedCard: any, container?: any): Promise<void> {
        const quoteProductId = data?.quote_product_id;
        const componentProductId = this.componentAllocation?.componentProductId;
        if (!quoteProductId || !componentProductId) {
            return;
        }

        /*
         * Any order's slice can be dragged, not just this one's — the same as an ordinary product,
         * whose cards also list every order and let you move any of them. It is also the point of
         * the view: freeing stock an earlier order is holding is how an urgent later order gets it,
         * and that is doable here without leaving the screen.
         */

        const target = container ? container.id : selectedCard;
        let availableQty: any;
        if (selectedCard === 'Stock On Hand') {
            availableQty = this.quantityData?.onHandStock?.available_qty;
        } else if (container) {
            availableQty = container?.available_qty;
        }

        // Same modal, same Split Stock affordance as an ordinary product.
        let transfer: any;
        try {
            transfer = await this.transfer(data, selectedCard, availableQty);
        } catch (e) {
            return;
        }
        if (!transfer) {
            return;
        }
        const qty = +transfer.splitStock || +transfer.quantity;

        this.isLoading = true;
        this.stockAllocationService.moveComponentSlice(quoteProductId, {
            component_product_id: componentProductId,
            from: this.componentSourceOf(this.selectedCard),
            to: this.componentSourceOf(target),
            quantity: qty,
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.alertService.success(this.constants.STOCK_ALLOCATION_CONSTANTS.PRODUCT_DRAG_DROP);
                this.loadComponentBoard();
                // The parent line's own position and every sibling component can have changed.
                this.dropActionCompleted.emit({
                    quoteProductId,
                    componentProductId,
                    orderId: this.orderId,
                });
            },
            error: (err: any) => {
                this.isLoading = false;
                this.alertService.error(err?.error?.message || 'Could not move that component.');
                this.loadComponentBoard();
            }
        });
    }

    transfer(product: any, selectedCard: any, availableQty?: any) {
        return new Promise(async (resolve, reject) => {
            if (selectedCard == 'Unassigned Stock') {
                let modalRef: any = this.bsModalService.show(ConfirmModalComponent);
                modalRef.content.title = 'Confirm Transfer';
                modalRef.content.message = 'Are you sure you want to transfer this product?';
                modalRef.content.onClose.subscribe((result) => {
                    if (result) {
                        resolve({quantity: product.quantity});
                    } else reject(null);
                })
            } else {
                const instance = this.modalService.open(TransferProductModalComponent);
                instance.componentInstance.showSplitStockOption = true;
                // Disable splitting only for SO orders (Unleashed-issued SO-xxxxx numbers,
                // same prefix check the backend uses). O-WEB / S-WEB / other records keep
                // the Split Stock option.
                instance.componentInstance.disableSplitStock = !!product?.quote?.order_number?.startsWith(this.constants.SALES_ORDER_NUMBER_PREFIX);
                instance.componentInstance.size = product.quantity;
                instance.componentInstance.uom = product?.product?.unit_of_measurement?.name;
                instance.componentInstance.product = product.product; // Pass the product object
                instance.componentInstance.availableQty = availableQty;
                instance.result.then((result) => {
                    resolve(result);
                }).catch((err) => reject(null))
            }
        })
    }

    openOrderModal(id: any) {
        const instance = this.modalService.open(PreviewDetailComponent, {
            centered: false,
            windowClass: 'quote-order-preview-model',
            backdrop: 'static',
        });
        instance.componentInstance.modelOrderId = id;
        instance.componentInstance.isStockAllocationSection = true;
        instance.componentInstance.isDisplayExpandableContent = true;
    }

    taskHasCategory(tasks: any[]): boolean {
        return tasks.some(task => task.taskCategory != null);
    }

    convertToSlug(text: string): string {
        return text ? text.toLowerCase().replace(/\s+/g, '-').concat('-status') : '';
    }

    containerDelayHistory(container: any): any[] {
        return containerDelayHistory(container);
    }

    openDelayHistory(container: any): void {
        openContainerDelayHistory(this.modalService, container);
    }

    /**
     * Calculate container quantities from its products
     * This is needed when page refreshes and container already has products
     */
    calculateContainerQuantities(container: any) {
        if (!container) return;

        // Calculate allocated_qty from products (sum of all product quantities in container)
        if (container.products && container.products.length > 0) {
            container.allocated_qty = container.products.reduce((total: number, product: any) => {
                return total + (parseFloat(product.quantity) || 0);
            }, 0);
            container.allocated_qty = +container.allocated_qty.toFixed(2);
        } else {
            // Initialize allocated_qty if no products
            if (container.allocated_qty === undefined || container.allocated_qty === null) {
                container.allocated_qty = 0;
            }
        }

        // For total_qty, calculate it from the container's own data
        // The total capacity should be the sum of ALL products that were originally added to this container
        // This includes both currently allocated products and any that might have been moved elsewhere

        // If container has a valid total_qty from backend, use it
        if (container.total_qty && container.total_qty > 0) {
            // Keep the existing total_qty from backend
        } else {
            // // If no total_qty or it's 0, calculate from available data within the container
            // // Look for container_product array or similar that might contain all original products
            // let calculatedTotal = 0;
            //
            // if (container.container_product && container.container_product.length > 0) {
            //   // Sum from container_product array if available
            //   calculatedTotal = container.container_product.reduce((total: number, cp: any) => {
            //     return total + (parseFloat(cp.quantity) || 0);
            //   }, 0);
            // } else if (container.products && container.products.length > 0) {
            //   // Fallback: use current products as minimum total
            //   calculatedTotal = container.allocated_qty;
            // }
            //
            // container.total_qty = +calculatedTotal.toFixed(2);


            let calculatedTotal = 0;
            const containerProducts = container.container_product;
            if (containerProducts?.length) {
                // Fast for-loop (better than .reduce() when filtering)
                for (let i = 0; i < containerProducts.length; i++) {
                    const cp = containerProducts[i];
                    if (cp.product_id === this.productId) {
                        const qty = parseFloat(cp.quantity);
                        if (!isNaN(qty)) calculatedTotal += qty;
                    }
                }
            } else if (container.products?.length) {
                // Fallback
                calculatedTotal = container.allocated_qty || 0;
            }

            // Use Math.round instead of toFixed (avoids string conversion)
            container.total_qty = Math.round(calculatedTotal * 100) / 100;
        }

        // Ensure total_qty is at least as much as allocated_qty (prevent negative available_qty)
        // if (container.total_qty < container.allocated_qty) {
        //   container.total_qty = container.allocated_qty;
        // }

        // Calculate available_qty (remaining capacity)
        container.available_qty = +(container.total_qty - container.allocated_qty).toFixed(2);
    }

    checkStockOnHandActive(dataList: any) {
        return (dataList?.findIndex(item => item?.quote?.id === this.orderId) > -1);
    }

    checkContainerActive(dataList: any) {
        return (dataList?.findIndex(item => +item?.quote_id === +this.orderId) > -1);
    }

    /**
     * Assembled cards: active where the open line's own units sit — the rows the board flags
     * `is_this_order`. The ordinary rule reads the line's stock_available / container_id inputs, which
     * the assembled panel is never given, so Unassigned always read as active. An assembled line can
     * sit on several cards at once, and each of them is then active.
     */
    isAssembledCardActive(dataList: any): boolean {
        return (dataList ?? []).some((row: any) => !!row?.is_this_order);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

}
